/**
 * A single-series column chart (inline SVG) with its table twin — the KPI dashboard's trends.
 *
 * - Fits its container: it measures its width and, where one bar per point would be too thin
 *   (a phone, a 90-day window), asks `regroup` for fewer, wider bars (runs of days).
 * - Marks: bars at most 24px wide with a 4px rounded top, square at the baseline; hairline solid
 *   gridlines; only the tallest bar carries its value, the axis and the readout carry the rest.
 * - Reading a bar: pointer hover (or a tap) and the arrow keys (once the chart has focus) show the
 *   same readout, which is also announced. `asTable` shows every value as a table instead.
 */

import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { niceTicks } from "../../lib/kpi";

export interface ColumnBar {
  key: string;
  /** Under the axis ("19 Oct", "14:00"). */
  label: string;
  /** The readout's heading ("19 Oct", "13 – 19 Oct", "14:00 – 15:00"). */
  title: string;
  value: number;
  /** The plotted value as read out ("₹12,400.00"). */
  valueText: string;
  /** Further figures for the readout and the table ("Paid orders" → "18"). */
  details?: { label: string; value: string }[];
}

const AXIS_W = 48; // y-axis labels
const RIGHT = 6;
const TOP = 22; // room for the tallest bar's label
const BOTTOM = 24; // x-axis labels
const MIN_SLOT = 10; // px per bar, gap included
const LABEL_W = 58; // room per x-axis label

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = (w: number, h: number) =>
      setSize((s) => (s.width === Math.floor(w) && s.height === Math.floor(h) ? s : { width: Math.floor(w), height: Math.floor(h) }));
    const box = el.getBoundingClientRect();
    read(box.width, box.height);
    const observer = new ResizeObserver(([entry]) => read(entry.contentRect.width, entry.contentRect.height));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

/** A column with a rounded top (4px, less on a thin or short bar) and a square foot. */
function columnPath(x: number, base: number, w: number, h: number): string {
  if (h <= 0) return "";
  const r = Math.min(4, w / 2, h);
  const top = base - h;
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${base}Z`;
}

export function ColumnChart({
  bars,
  seriesLabel,
  axisFormat,
  integer = false,
  height: fixedHeight = 210,
  fill = false,
  className = "",
  regroup,
  asTable = false,
  summary,
}: {
  bars: ColumnBar[];
  /** What the bars measure ("Sales"); names the readout row and the table column. */
  seriesLabel: string;
  axisFormat: (v: number) => string;
  /** Counts: whole-number ticks only. */
  integer?: boolean;
  /** The chart's height (x-axis labels included); with `fill`, the least it takes. */
  height?: number;
  /** Take the height the layout gives (e.g. a card stretched to its row) instead of a fixed one.
   *  The plot is laid over the space rather than inside it, so it never holds the row taller. */
  fill?: boolean;
  className?: string;
  /** Fewer, wider bars when the plot can't fit one per point. */
  regroup?: (maxBars: number) => ColumnBar[];
  asTable?: boolean;
  /** One sentence for screen readers ("Sales per day, 13 – 19 Oct; highest ₹12.4K on 15 Oct."). */
  summary: string;
}) {
  const [ref, { width, height: measured }] = useSize<HTMLDivElement>();
  const height = fill ? Math.max(fixedHeight, measured) : fixedHeight;
  const [active, setActive] = useState<number | null>(null);

  const plotW = Math.max(0, width - AXIS_W - RIGHT);
  const maxBars = Math.max(1, Math.floor(plotW / MIN_SLOT));
  const shown = regroup && bars.length > maxBars ? regroup(maxBars) : bars;
  const n = shown.length;
  const maxValue = Math.max(0, ...shown.map((b) => b.value));
  const ticks = niceTicks(maxValue, { integer });
  const top = ticks[ticks.length - 1] || 1;
  const plotH = height - TOP - BOTTOM;
  const base = TOP + plotH;
  const slot = n ? plotW / n : 0;
  const barW = Math.max(2, Math.min(24, slot - Math.max(2, slot * 0.3)));
  const yOf = (v: number) => base - (v / top) * plotH;
  const peak = maxValue > 0 ? shown.findIndex((b) => b.value === maxValue) : -1;
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / LABEL_W))));
  const current = active !== null && active < n ? active : null;

  const pick = (e: PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - box.left - AXIS_W) / (slot || 1));
    setActive(i >= 0 && i < n ? i : null);
  };
  const step = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = n - 1;
    const next = { ArrowRight: (current ?? -1) + 1, ArrowLeft: (current ?? n) - 1, Home: 0, End: last }[e.key];
    if (next === undefined || n === 0) return;
    e.preventDefault();
    setActive(Math.max(0, Math.min(last, next)));
  };

  if (asTable) {
    const extra = bars[0]?.details?.map((d) => d.label) ?? [];
    return (
      // The measured wrapper stays mounted, so switching back to the chart has its width.
      <div ref={ref} className={`w-full ${className}`}>
        <div className="table-wrap max-h-80 overflow-y-auto shadow-none">
          <table className="table">
            <caption className="sr-only">{summary}</caption>
            <thead className="sticky top-0">
              <tr>
                <th scope="col">Period</th>
                <th scope="col" className="text-right">{seriesLabel}</th>
                {extra.map((label) => (
                  <th key={label} scope="col" className="text-right">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bars.map((b) => (
                <tr key={b.key}>
                  <td className="whitespace-nowrap">{b.title}</td>
                  <td className="text-right tabular-nums">{b.valueText}</td>
                  {b.details?.map((d) => (
                    <td key={d.label} className="text-right tabular-nums">{d.value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const bar = current !== null ? shown[current] : null;
  const barCenter = (i: number) => AXIS_W + i * slot + slot / 2;
  // The readout sits over the bar, kept inside the chart near either edge.
  const readoutX = current !== null ? barCenter(current) : 0;
  const anchor = readoutX < 90 ? "left" : readoutX > width - 90 ? "right" : "center";

  return (
    <div
      ref={ref}
      className={`relative w-full select-none rounded-lg outline-offset-4 ${className}`}
      style={fill ? { minHeight: fixedHeight } : { height }}
      tabIndex={0}
      role="group"
      aria-label={`${summary} Use the left and right arrow keys to read each bar.`}
      onKeyDown={step}
      onBlur={() => setActive(null)}
    >
      {width > 0 && (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="absolute left-0 top-0 block touch-pan-y"
          aria-hidden
          onPointerMove={pick}
          onPointerDown={pick}
          onPointerLeave={(e) => e.pointerType === "mouse" && setActive(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={AXIS_W}
                x2={width - RIGHT}
                y1={Math.round(yOf(t)) + 0.5}
                y2={Math.round(yOf(t)) + 0.5}
                className={t === 0 ? "stroke-line-strong" : "stroke-line"}
                strokeWidth={1}
              />
              <text x={AXIS_W - 8} y={yOf(t)} dy="0.32em" textAnchor="end" className="fill-ink-muted text-[11px] tabular-nums">
                {axisFormat(t)}
              </text>
            </g>
          ))}
          {shown.map((b, i) => {
            const h = (b.value / top) * plotH;
            const x = AXIS_W + i * slot + (slot - barW) / 2;
            return (
              <path
                key={b.key}
                d={columnPath(x, base, barW, h)}
                className={`transition-colors ${i === current ? "fill-brand-700" : "fill-brand-500"}`}
              />
            );
          })}
          {peak >= 0 && n > 1 && (
            <text
              x={Math.min(Math.max(barCenter(peak), AXIS_W + 16), width - RIGHT - 16)}
              y={yOf(maxValue) - 7}
              textAnchor="middle"
              className="fill-ink-soft text-[11px] font-semibold tabular-nums"
            >
              {axisFormat(maxValue)}
            </text>
          )}
          {shown.map((b, i) =>
            (n - 1 - i) % labelEvery === 0 ? (
              <text
                key={b.key}
                x={Math.min(Math.max(barCenter(i), AXIS_W + 14), width - RIGHT - 14)}
                y={height - 6}
                textAnchor="middle"
                className="fill-ink-muted text-[11px]"
              >
                {b.label}
              </text>
            ) : null,
          )}
          {current !== null && (
            <line
              x1={barCenter(current)}
              x2={barCenter(current)}
              y1={TOP - 4}
              y2={base}
              className="stroke-brand-200"
              strokeWidth={1}
            />
          )}
        </svg>
      )}
      {bar && (
        <div
          className={`pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-xl border border-line bg-surface px-3 py-2 text-xs shadow-raised ${
            anchor === "center" ? "-translate-x-1/2" : anchor === "right" ? "-translate-x-full" : ""
          }`}
          style={{ left: readoutX }}
        >
          <div className="text-ink-muted">{bar.title}</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="h-0.5 w-3 rounded-full bg-brand-500" aria-hidden />
            <span className="font-semibold tabular-nums text-ink">{bar.valueText}</span>
            <span className="text-ink-muted">{seriesLabel}</span>
          </div>
          {bar.details?.map((d) => (
            <div key={d.label} className="mt-0.5 flex items-center gap-2 pl-5">
              <span className="font-medium tabular-nums text-ink-soft">{d.value}</span>
              <span className="text-ink-muted">{d.label}</span>
            </div>
          ))}
        </div>
      )}
      <p className="sr-only" aria-live="polite">
        {bar ? `${bar.title}: ${bar.valueText} ${seriesLabel}${bar.details?.map((d) => `, ${d.value} ${d.label}`).join("") ?? ""}` : ""}
      </p>
    </div>
  );
}
